import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CalendarPlus, Save, Trash2 } from "lucide-react";
import RecordSection from "@/components/RecordSection";
import { PLAN_CONFIG, DEFAULT_PLAN_WEEKS, SEMESTER_PLAN_DATA } from "@/lib/sectionConfigs";
import { useAuth } from "@/contexts/AuthContext";
import { staffTitles } from "@/lib/staffTitles";

const PLAN_TABS = {
  first: { label: "الفصل الدراسي الأول", title: "خطة الفصل الدراسي الأول 2026-2027م" },
  second: { label: "الفصل الدراسي الثاني", title: "خطة الفصل الدراسي الثاني 2026-2027م" },
  legacy: { label: "الخطة السابقة", title: PLAN_CONFIG.title },
};

export default function SemesterPlan({ readOnly = false, counselorId = null, counselorName = "", counselorGender = "", schoolKey = null, schoolName = "" }) {
  const { user } = useAuth();
  readOnly = readOnly || user?.role !== "counselor" || Boolean(counselorId && counselorId !== user?.id);
  const ownerGender = counselorGender || (!counselorId && user?.role === "counselor" ? user.gender : "");
  const titles = staffTitles(ownerGender);
  const planCounselorName = counselorName || user?.name || "";
  const effectiveCounselorId = counselorId || (user?.role === "counselor" ? user.id : null);
  const principalNameStorageKey = `plan_principal_name_${schoolKey || "unknown"}_${counselorGender || user?.gender || "unknown"}`;
  const requestedSemester = new URLSearchParams(window.location.search).get("semester");
  const [semester, setSemester] = useState(() => Object.hasOwn(PLAN_TABS, requestedSemester) ? requestedSemester : "first");
  const [counts, setCounts] = useState({});
  const [importing, setImporting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [principalName, setPrincipalName] = useState(() => localStorage.getItem(principalNameStorageKey) || "");

  const planConfig = useMemo(() => {
    if (semester === "legacy") return PLAN_CONFIG;
    return {
      ...PLAN_CONFIG,
      key: `plan_task_1448_${semester}`,
      title: PLAN_TABS[semester].title,
    };
  }, [semester]);

  const handleRecordCount = useCallback(
    (count) => setCounts((current) => ({ ...current, [semester]: count })),
    [semester],
  );

  const loadCounts = useCallback(async () => {
    try {
      const requests = Object.keys(PLAN_TABS).map(async (key) => {
        const params = { type: key === "legacy" ? PLAN_CONFIG.key : `plan_task_1448_${key}` };
        if (counselorId) params.counselor_id = counselorId;
        const { data } = await api.get("/records", { params });
        return [key, data.length];
      });
      setCounts(Object.fromEntries(await Promise.all(requests)));
    } catch (error) {
      toast.error(formatErr(error));
    }
  }, [counselorId]);

  useEffect(() => { loadCounts(); }, [loadCounts, refreshKey]);

  useEffect(() => {
    let active = true;
    const params = effectiveCounselorId ? { counselor_id: effectiveCounselorId } : {};
    api.get("/school-principal", { params })
      .then(({ data }) => {
        if (!active) return;
        const savedName = localStorage.getItem(principalNameStorageKey);
        setPrincipalName(savedName || data.name || "");
      })
      .catch((error) => toast.error(formatErr(error)));
    return () => { active = false; };
  }, [effectiveCounselorId, principalNameStorageKey]);

  const importPlan = async () => {
    const tasks = semester === "legacy"
      ? DEFAULT_PLAN_WEEKS.map(([week, program, target, works]) => ({ week, program, target, works, status: "لم ينفذ", notes: "" }))
      : SEMESTER_PLAN_DATA[semester].map(([week, date, program, target, works]) => ({
        week, date, program, target, works, status: "لم ينفذ", notes: "",
      }));
    setImporting(true);
    try {
      await api.post("/records/bulk", { type: planConfig.key, records: tasks });
      toast.success(`تمت إضافة خطة ${PLAN_TABS[semester].label}، ويمكنك تعديل البنود أو حذفها`);
      setCounts((current) => ({ ...current, [semester]: tasks.length }));
      setRefreshKey((key) => key + 1);
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setImporting(false);
    }
  };

  const deleteWholePlan = async () => {
    const title = PLAN_TABS[semester].label;
    if (!window.confirm(`سيتم حذف جميع بنود ${title}. لا يمكن التراجع عن الحذف. هل تريد المتابعة؟`)) return;
    setImporting(true);
    try {
      const { data } = await api.delete("/records/bulk", { params: { type: planConfig.key } });
      setCounts((current) => ({ ...current, [semester]: 0 }));
      setRefreshKey((key) => key + 1);
      toast.success(`تم حذف الخطة كاملة (${data.deleted} بنداً)`);
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setImporting(false);
    }
  };

  const savePrincipalName = (value) => {
    setPrincipalName(value);
    localStorage.setItem(principalNameStorageKey, value);
  };

  return (
    <div className="space-y-4">
      <div className="no-print flex flex-wrap gap-2" role="tablist" aria-label="الفصل الدراسي">
        {Object.entries(PLAN_TABS).filter(([key]) => key !== "legacy" || counts.legacy > 0).map(([key, tab]) => (
          <Button
            key={key}
            type="button"
            role="tab"
            aria-selected={semester === key}
            variant={semester === key ? "default" : "outline"}
            onClick={() => setSemester(key)}
            className={`rounded-xl ${semester === key ? "bg-[#3E6A56] hover:bg-[#2D4F40]" : ""}`}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {!readOnly && counts[semester] === 0 && semester !== "legacy" && (
        <div className="flex flex-col gap-3 rounded-2xl border border-[#C97052]/30 bg-[#C97052]/5 p-4 sm:flex-row sm:items-center sm:justify-between no-print">
          <div className="text-sm text-[#1A201C]">
            أضف بنود الخطة المرفقة لهذا الفصل كبنود قابلة للتعديل والحذف والطباعة، أو أضف البنود يدوياً.
          </div>
          <Button onClick={importPlan} disabled={importing} className="shrink-0 rounded-xl bg-[#C97052] hover:bg-[#B05F42]">
            <CalendarPlus className="me-2 h-4 w-4" />{importing ? "جاري الإضافة..." : `إضافة الخطة (${SEMESTER_PLAN_DATA[semester].length} أسبوعاً)`}
          </Button>
        </div>
      )}
      {!readOnly && semester === "legacy" && counts.legacy === 0 && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-[#C97052]/30 bg-[#C97052]/5 p-4 no-print">
          <span className="text-sm">استيراد نموذج الأسابيع السابق.</span>
          <Button onClick={importPlan} disabled={importing} className="rounded-xl bg-[#C97052] hover:bg-[#B05F42]">
            <CalendarPlus className="me-2 h-4 w-4" />{importing ? "جاري الإضافة..." : "استيراد النموذج السابق"}
          </Button>
        </div>
      )}

      {!readOnly && counts[semester] > 0 && (
        <div className="no-print flex justify-end">
          <Button type="button" variant="outline" onClick={deleteWholePlan} disabled={importing} className="rounded-xl border-red-200 text-red-700 hover:bg-red-50">
            <Trash2 className="me-2 h-4 w-4" /> حذف الخطة كاملة
          </Button>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#e6e2d8] p-4 no-print">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label className="mb-1.5 block">اسم {titles.counselor}</Label>
            <Input value={planCounselorName} readOnly className="rounded-xl bg-[#F9F8F6] border-[#e6e2d8]" />
          </div>
          <div>
            <Label className="mb-1.5 block">اسم {titles.principal}</Label>
            <Input value={principalName} onChange={(event) => savePrincipalName(event.target.value)} readOnly={readOnly} placeholder="يظهر في أسفل الصفحة" className="rounded-xl bg-white border-[#e6e2d8]" />
          </div>
        </div>
        {!readOnly && <div className="mt-3 flex items-center gap-2 text-xs text-[#4A554E]"><Save className="h-3.5 w-3.5" /> يحفظ اسم {titles.principal} تلقائياً لهذا الجهاز.</div>}
      </div>

      <RecordSection
        key={`${planConfig.key}-${refreshKey}`}
        config={planConfig}
        readOnly={readOnly}
        counselorId={counselorId}
        schoolKey={schoolKey}
        schoolName={schoolName}
        counselorGender={ownerGender}
        pdfFooter={{ counselor: planCounselorName, principal: principalName, counselorGender: ownerGender }}
        submittable
        onRecordsChange={handleRecordCount}
      />
    </div>
  );
}
